import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Layers, Loader2, Save, RotateCcw, Trash2, AlertCircle } from 'lucide-react';

import {
    fetchItemDepGrid,
    fetchItemDepByType,
    submitItemDepreciationPercent,
    clearItemDepByType,
    clearItemDepSaveResult,
    selectItemDepGridList,
    selectItemDepCurrentPercent,
    selectItemDepSaveResult,
    selectItemDepGridLoading,
    selectItemDepGridError,
    selectItemDepByTypeLoading,
    selectItemDepSaveLoading,
    selectItemDepSaveError,
} from '../../slices/purchaseSlice/itemDepreciationConfigSlice';
import { showToast } from '../../utilities/toastUtilities';

const ACTION_OPTIONS = ['Add', 'Edit'];
const TYPE_OPTIONS = [
    { value: 'Issue', label: 'Issue' },
    { value: 'Receive', label: 'Receive' },
];
const PERCENT_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const inputCls =
    'w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';

const ItemDepreciationConfig = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;

    const gridList = useSelector(selectItemDepGridList);
    const currentPercent = useSelector(selectItemDepCurrentPercent);
    const saveResult = useSelector(selectItemDepSaveResult);

    const gridLoading = useSelector(selectItemDepGridLoading);
    const gridError = useSelector(selectItemDepGridError);
    const byTypeLoading = useSelector(selectItemDepByTypeLoading);
    const saveLoading = useSelector(selectItemDepSaveLoading);
    const saveError = useSelector(selectItemDepSaveError);

    const [action, setAction] = useState('');
    const [type, setType] = useState('');
    const [percentage, setPercentage] = useState('');
    const [formError, setFormError] = useState('');
    const [deletingType, setDeletingType] = useState(null);

    const load = () => dispatch(fetchItemDepGrid());

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    useEffect(() => {
        if (action === 'Edit' && type) {
            dispatch(fetchItemDepByType(type));
        } else {
            dispatch(clearItemDepByType());
        }
        setPercentage('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, type]);

    useEffect(() => {
        if (action !== 'Edit') return;
        if (currentPercent === null) return;
        if (currentPercent > 0 && PERCENT_OPTIONS.includes(currentPercent)) {
            setPercentage(String(currentPercent));
        } else if (currentPercent > 0) {
            setPercentage('');
        } else {
            setFormError('Depreciation percentage does not exist for this type yet.');
            setPercentage('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPercent]);

    useEffect(() => {
        if (saveResult) {
            showToast('success', 'Saved successfully.');
            dispatch(clearItemDepSaveResult());
            resetForm();
            load();
        }
        if (saveError) {
            const msg = typeof saveError === 'string' ? saveError : 'Failed to save item depreciation configuration.';
            setFormError(msg);
            showToast('error', msg);
            dispatch(clearItemDepSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError, dispatch]);

    const resetForm = () => {
        setAction('');
        setType('');
        setPercentage('');
        setFormError('');
    };

    const handleActionChange = (e) => {
        setFormError('');
        setAction(e.target.value);
        setType('');
    };

    const handleTypeChange = (e) => {
        setFormError('');
        setType(e.target.value);
    };

    const handleSubmit = () => {
        if (!action) { setFormError('Select Action'); return; }
        if (!type) { setFormError('Select Type'); return; }
        if (!percentage) { setFormError('Select Percentage'); return; }
        setFormError('');

        dispatch(submitItemDepreciationPercent({
            Percentage: parseInt(percentage, 10),
            Type: type,
            Action: action,
            CreatedBy: currentUser,
            RoleId: roleId,
        }));
    };

    const handleDelete = (row) => {
        setDeletingType(row.Type);
        dispatch(submitItemDepreciationPercent({
            Percentage: row.Percentage,
            Type: row.Type,
            Action: 'Delete',
            CreatedBy: currentUser,
            RoleId: roleId,
        }));
    };

    useEffect(() => {
        if (!saveLoading) setDeletingType(null);
    }, [saveLoading]);

    const percentOptionsWithLabel = useMemo(
        () => PERCENT_OPTIONS.map((p) => ({ value: p, label: p === 100 ? 'FullValue (100)' : String(p) })),
        []
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-5xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <Layers className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Configuration
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">Item Depreciation Configuration</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                Issue / Receive item depreciation percentage used during item transfer.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto space-y-4">

                {/* Add / Edit form */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <Layers className="h-4 w-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">Add / Edit Depreciation Percentage</h2>
                    </div>

                    <div className="p-4 md:p-5">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Action
                                </label>
                                <select value={action} onChange={handleActionChange} className={inputCls}>
                                    <option value="">-Please Select-</option>
                                    {ACTION_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Type
                                </label>
                                <select value={type} onChange={handleTypeChange} disabled={!action} className={inputCls}>
                                    <option value="">-Please Select-</option>
                                    {TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Percentage
                                </label>
                                <div className="relative">
                                    <select
                                        value={percentage}
                                        onChange={(e) => { setFormError(''); setPercentage(e.target.value); }}
                                        disabled={!type || byTypeLoading}
                                        className={inputCls}
                                    >
                                        <option value="">-Please Select-</option>
                                        {percentOptionsWithLabel.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                    {byTypeLoading && (
                                        <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSubmit}
                                    disabled={saveLoading}
                                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {saveLoading && !deletingType ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                    Submit
                                </button>
                                <button
                                    onClick={resetForm}
                                    disabled={saveLoading}
                                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                                >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    Reset
                                </button>
                            </div>
                        </div>

                        {formError && (
                            <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800/50 text-sm text-rose-600 dark:text-rose-400">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                {formError}
                            </div>
                        )}
                    </div>
                </div>

                {/* Grid of configured percentages */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">Configured Percentages</h2>
                    </div>

                    {gridLoading ? (
                        <div className="py-16 flex flex-col items-center justify-center gap-3">
                            <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
                            <span className="text-sm text-gray-400">Loading configured percentages…</span>
                        </div>
                    ) : gridError ? (
                        <div className="py-10 text-center text-sm text-rose-500">{gridError}</div>
                    ) : gridList.length === 0 ? (
                        <div className="py-16 text-center text-gray-400 dark:text-gray-500 text-sm">
                            No data found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Percentage</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-center w-24">Delete</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {gridList.map((row) => {
                                        const isDeleting = deletingType === row.Type && saveLoading;
                                        return (
                                            <tr key={row.Type} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                                <td className="px-4 py-3 text-gray-700 dark:text-gray-200 font-semibold">{row.Type}</td>
                                                <td className="px-4 py-3 text-right font-semibold text-indigo-700 dark:text-indigo-400">{row.Percentage}%</td>
                                                <td className="px-4 py-3 text-center">
                                                    <button
                                                        onClick={() => handleDelete(row)}
                                                        disabled={saveLoading}
                                                        title="Delete"
                                                        className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 disabled:opacity-50 transition-colors"
                                                    >
                                                        {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ItemDepreciationConfig;
